// Filtering/sorting for the Programme Builder's source browser (the left panel):
// which saved programme rows each tab shows, the filter dropdown options, and how
// rows are matched against the filters, flags and search box.
//
// Rows are the saved programmes from GET /programmes (programme_details plus
// `archived`, `archived_at`, `account_name`, and for block/part/module rows
// `slide_count`, `geography` (a list) and `flags`).

import { parseDbDate } from '../../utils/dates';

export const SOURCE_TABS = [
  { key: 'slides', label: 'Slides' },
  { key: 'blocks', label: 'Blocks' },
  { key: 'parts', label: 'Parts' },
  { key: 'modules', label: 'Modules' },
  { key: 'programmes', label: 'Programmes' },
  { key: 'archive', label: 'Archive' },
];

export const DEFAULT_SOURCE_TAB = 'slides';

// Saved programme types listed on each (non-slides, non-archive) tab.
const TAB_TYPES = {
  blocks: ['block'],
  parts: ['part'],
  modules: ['module'],
  programmes: ['template', 'custom'],
};

export const TYPE_LABELS = {
  block: 'Block',
  part: 'Part',
  module: 'Module',
  template: 'Template',
  custom: 'Custom',
};

const GEOGRAPHY_PREFIX = 'Geography - ';

// The filter dropdowns shown on each tab, in order. `field` is the row property
// filtered on; option labels for coded fields come from the row values, which the
// backend already sends as labels.
export const TAB_FILTER_FIELDS = {
  blocks: [
    { field: 'audience', label: 'Audience' },
    { field: 'delivery', label: 'Delivery' },
  ],
  programmes: [
    { field: 'type', label: 'Type' },
    { field: 'account', label: 'Account' },
    { field: 'year', label: 'Year' },
    { field: 'month', label: 'Month' },
    { field: 'audience', label: 'Audience' },
    { field: 'delivery', label: 'Delivery' },
    { field: 'access', label: 'Access' },
  ],
  archive: [
    { field: 'type', label: 'Type' },
  ],
};

// Tabs whose rows can be filtered by (slide) flags.
export const TABS_WITH_FLAG_FILTER = ['slides', 'blocks'];

// Archived means it has an archive date (programme_details.archived_at).
export const isArchivedRow = (row) => Boolean(row?.archived_at);

export const isSourceTab = (key) => SOURCE_TABS.some((t) => t.key === key);

// The saved programme rows a tab lists (before filters/search).
export function rowsForTab(programmes, tab) {
  const list = Array.isArray(programmes) ? programmes : [];
  if (tab === 'archive') {
    return list.filter((p) => p && p.type !== 'file' && isArchivedRow(p));
  }
  const types = TAB_TYPES[tab];
  if (!types) return [];
  return list.filter((p) => p && types.includes(p.type) && !isArchivedRow(p));
}

// The flag names a block row is filtered/shown by: its slides' flags plus its
// geography as "Geography - <label>" flags (see rowGeography).
export function rowFlagNames(row) {
  const flags = Array.isArray(row?.flags) ? row.flags : [];
  return [...flags, ...rowGeography(row).map((label) => `${GEOGRAPHY_PREFIX}${label}`)];
}

// Order flags are listed in under slides and saved items: Geography, then Topic,
// then Company, then any other category; alphabetical within each.
const FLAG_CATEGORY_ORDER = ['Geography', 'Topic', 'Company'];

const flagCategoryRank = (name) => {
  const category = String(name || '').split(' - ')[0].trim();
  const i = FLAG_CATEGORY_ORDER.indexOf(category);
  return i === -1 ? FLAG_CATEGORY_ORDER.length : i;
};

export function sortFlagNames(names) {
  return [...(names || [])].sort((a, b) => (
    flagCategoryRank(a) - flagCategoryRank(b)
    || String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
  ));
}

// Label for a flag in those lists: "Topic - Economics" -> "Economics" for the
// listed categories (their order makes the category clear); other categories
// keep their full name.
export function flagDisplayLabel(name) {
  const text = String(name || '');
  const sep = text.indexOf(' - ');
  if (sep === -1) return text;
  return FLAG_CATEGORY_ORDER.includes(text.slice(0, sep).trim()) ? text.slice(sep + 3).trim() : text;
}

// The flags listed under a slide: its Geography, Topic and Company flags, in that
// order, as display labels.
export function slideDisplayFlags(flagNames) {
  return sortFlagNames(
    (flagNames || []).filter((name) => flagCategoryRank(name) < FLAG_CATEGORY_ORDER.length)
  ).map(flagDisplayLabel);
}

// The flags listed under a saved item in the source list: blocks show their
// geography, then their slides' other flags in the same order (Topic, Company,
// then the rest); parts, modules and programmes show only their geography.
// Entries are display labels, except that two or more Company flags are combined
// into one { label: 'N companies', tooltip } entry whose hover text lists them,
// one per line, A → Z.
export function rowDisplayFlags(row) {
  const geography = rowGeography(row).map(geographyDisplayLabel);
  if (row?.type !== 'block') return geography;
  const others = sortFlagNames(row?.flags);
  const isCompany = (name) => String(name || '').split(' - ')[0].trim() === 'Company';
  const companies = others.filter(isCompany).map(flagDisplayLabel);
  if (companies.length < 2) return [...geography, ...others.map(flagDisplayLabel)];
  const combined = {
    label: `${companies.length} companies`,
    tooltip: [...companies].sort(compareNames).join('\n'),
  };
  // Companies sit together (after Topic), so the combined entry takes their place.
  const at = others.findIndex(isCompany);
  return [
    ...geography,
    ...others.slice(0, at).map(flagDisplayLabel),
    combined,
    ...others.filter((name, i) => i > at && !isCompany(name)).map(flagDisplayLabel),
  ];
}

// A saved item's geography labels (from the backend's slide summary): ["Global"],
// the regions every regional slide in it shares (e.g. ["UK"], ["EU", "UK"]), or
// ["Unknown"] when its regional slides have no region in common.
export const GEOGRAPHY_UNKNOWN = 'Unknown';
export function rowGeography(row) {
  const value = row?.geography;
  if (Array.isArray(value)) return value.length ? value : ['Global'];
  return [value || 'Global'];
}

// How a geography label reads under an item ("Unknown" -> "Unknown geography").
const geographyDisplayLabel = (label) => (label === GEOGRAPHY_UNKNOWN ? 'Unknown geography' : label);

// `mode` 'all': every selected flag must be present; 'any': at least one.
export function matchesFlagSelection(names, selectedNames, mode) {
  const selected = Array.isArray(selectedNames) ? selectedNames : [];
  if (selected.length === 0) return true;
  const have = new Set((names || []).map(String));
  return mode === 'any'
    ? selected.some((n) => have.has(String(n)))
    : selected.every((n) => have.has(String(n)));
}

const normalizeForSearch = (s) => String(s || '')
  .replace(/[‘’‚‛ʼ′]/g, "'")
  .replace(/[“”„‟″]/g, '"')
  .toLowerCase();

// True when every word of the search term appears in the row's name.
export function matchesNameSearch(row, searchTerm) {
  const terms = normalizeForSearch(searchTerm).split(/[\s,]+/).filter(Boolean);
  if (terms.length === 0) return true;
  const name = normalizeForSearch(row?.name);
  return terms.every((t) => name.includes(t));
}

// The value a row is filtered on for `field`, as a string ('' when unset).
// Account filters on the account id; everything else on the row value itself.
function rowFilterValue(row, field) {
  const v = row?.[field];
  return v === null || v === undefined ? '' : String(v);
}

// [{ value, label }] for one filter dropdown, built from the values actually
// present in `rows` (so empty choices never appear).
export function filterOptions(rows, field, { monthLabels = {} } = {}) {
  const seen = new Map();
  for (const row of rows || []) {
    const value = rowFilterValue(row, field);
    if (!value || seen.has(value)) continue;
    let label = value;
    if (field === 'type') label = TYPE_LABELS[value] || value;
    else if (field === 'account') label = row.account_name || `Account #${value}`;
    else if (field === 'month') label = monthLabels[Number(value)] || value;
    seen.set(value, label);
  }
  const options = Array.from(seen, ([value, label]) => ({ value, label }));
  if (field === 'year') return options.sort((a, b) => Number(b.value) - Number(a.value));
  if (field === 'month') return options.sort((a, b) => Number(a.value) - Number(b.value));
  if (field === 'type') {
    const order = Object.keys(TYPE_LABELS);
    return options.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
  }
  return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

// Rows matching every set dropdown filter ({ field: value }; '' = not filtered).
export function applyRowFilters(rows, filters) {
  const isSet = (v) => v !== '' && v !== null && v !== undefined;
  // Month only counts with a year (its dropdown is hidden until one is picked).
  const active = Object.entries(filters || {})
    .filter(([field, v]) => isSet(v) && (field !== 'month' || isSet(filters.year)));
  if (active.length === 0) return rows || [];
  return (rows || []).filter((row) => active.every(([field, v]) => rowFilterValue(row, field) === String(v)));
}

// ── Sorting ─────────────────────────────────────────────────────────────────
// One sort applies at a time (each tab remembers its own). Clicking the selected
// option reverses it; picking another starts it in its default direction. Slides
// default to Usage (most used first), Archive to Archived (newest first), every
// other tab to Name.
export const SORT_OPTIONS = {
  usage: { label: 'Usage', defaultDirection: 'desc', asc: 'Least used first', desc: 'Most used first' },
  name: { label: 'Name', defaultDirection: 'asc', asc: 'A → Z', desc: 'Z → A' },
  modified: { label: 'Modified', defaultDirection: 'desc', asc: 'Oldest first', desc: 'Newest first' },
  archived: { label: 'Archived', defaultDirection: 'desc', asc: 'Oldest first', desc: 'Newest first' },
};

export const DEFAULT_SORT = { key: 'name', direction: 'asc' };

// A tab's sort until the user picks one.
export function defaultSortForTab(tab) {
  if (tab === 'slides') return { key: 'usage', direction: 'desc' };
  if (tab === 'archive') return { key: 'archived', direction: 'desc' };
  return DEFAULT_SORT;
}

// The sort options offered on a tab (Usage only for slides, Archived only on the
// Archive tab).
export function sortKeysForTab(tab) {
  if (tab === 'slides') return ['usage', 'modified', 'name'];
  return tab === 'archive' ? ['archived', 'name'] : ['modified', 'name'];
}

// The sort actually used on a tab: the chosen one, or the tab's default if none
// is chosen or this tab doesn't offer it.
export function effectiveSort(tab, sort) {
  const key = sortKeysForTab(tab).includes(sort?.key) ? sort.key : defaultSortForTab(tab).key;
  const direction = sort?.key === key && (sort.direction === 'asc' || sort.direction === 'desc')
    ? sort.direction
    : SORT_OPTIONS[key].defaultDirection;
  return { key, direction };
}

// Click on a sort option: reverse it if it's already selected, otherwise select
// it in its default direction.
export function nextSort(currentSort, key) {
  if (currentSort?.key === key) {
    return { key, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: SORT_OPTIONS[key].defaultDirection };
}

// The value an item is sorted on. Slides (Slides tab) and saved programme rows
// name their fields differently. Dates are "YYYY-MM-DD HH:MM:SS" strings, which
// sort correctly as text; a programme never modified falls back to its created
// date.
function sortValue(item, key, tab) {
  if (key === 'usage') return Number(item?.slide_usage_count) || 0;
  if (key === 'name') {
    return tab === 'slides' ? (item?.slide_name || item?.slide_tag || '') : (item?.name || '');
  }
  if (key === 'modified') {
    return tab === 'slides' ? (item?.slide_modified || '') : (item?.modified_at || item?.created_at || '');
  }
  if (key === 'archived') return item?.archived_at || '';
  return '';
}

// Alphabetical order looks at letters only: digits, punctuation and spaces are
// ignored, so "2. Valuation" sorts as "Valuation" and "M&A" as "MA". Names with
// the same letters (e.g. "Block 2" / "Block 10") fall back to the full name, with
// numbers compared as numbers, so the order is still stable.
const lettersOnly = (value) => String(value || '').replace(/[^\p{L}]/gu, '');
export const compareNames = (a, b) => {
  const byLetters = lettersOnly(a).localeCompare(lettersOnly(b), undefined, { sensitivity: 'base' });
  if (byLetters !== 0) return byLetters;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
};

// A sorted copy of `items` for `tab`. Items with no value for the sort (e.g. no
// date, or a name with no letters) always go last (a usage count of 0 is a value,
// not missing); ties are broken by name, A → Z.
export function sortSourceItems(items, tab, sort) {
  const { key, direction } = effectiveSort(tab, sort);
  const sign = direction === 'desc' ? -1 : 1;
  return [...(items || [])].sort((a, b) => {
    const av = sortValue(a, key, tab);
    const bv = sortValue(b, key, tab);
    const isMissing = (v) => v === '' || v === null || v === undefined || (key === 'name' && !lettersOnly(v));
    const aMissing = isMissing(av);
    const bMissing = isMissing(bv);
    if (aMissing !== bMissing) return aMissing ? 1 : -1;
    const primary = key === 'name' ? compareNames(av, bv) : (av < bv ? -1 : (av > bv ? 1 : 0));
    if (primary !== 0) return primary * sign;
    return key === 'name' ? 0 : compareNames(sortValue(a, 'name', tab), sortValue(b, 'name', tab));
  });
}

// "2026-09-27 10:27:03" (UTC) -> "27 Sep 2026" (falls back to the raw value).
export function formatArchivedDate(value) {
  if (!value) return '';
  const d = parseDbDate(value);
  if (!d) return String(value);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
